import React from "react";
import { TrendingUp } from "lucide-react";
import { computeVolumeDiscount } from "../lib/volumeDiscount";

const LABELS = {
  fr: {
    subtotal: "Sous-total HT",
    discount: "Remise volume",
    net: "Total HT à payer",
    nextTier: "Ajoutez",
    nextTier2: "pour bénéficier de",
    savings: "Vous économisez",
    tierApplied: "Remise multi-enseignes appliquée",
  },
  en: {
    subtotal: "Subtotal (excl. VAT)",
    discount: "Volume discount",
    net: "Total (excl. VAT)",
    nextTier: "Add",
    nextTier2: "to unlock",
    savings: "You save",
    tierApplied: "Multi-sign discount applied",
  },
  de: {
    subtotal: "Zwischensumme (netto)",
    discount: "Mengenrabatt",
    net: "Zu zahlen (netto)",
    nextTier: "Fügen Sie",
    nextTier2: "hinzu für",
    savings: "Sie sparen",
    tierApplied: "Mehrschilder-Rabatt angewendet",
  },
  it: {
    subtotal: "Subtotale IVA esclusa",
    discount: "Sconto volume",
    net: "Totale da pagare",
    nextTier: "Aggiungi",
    nextTier2: "per ottenere",
    savings: "Risparmi",
    tierApplied: "Sconto multi-insegna applicato",
  },
};

const fmt = (v) => `${Math.round(v).toLocaleString("fr-CH")} CHF`;

/**
 * Affiche sous-total + remise volume (si applicable) + total net, avec un
 * message d'incitation au palier suivant.
 * Props :
 *   subtotal : number (sous-total HT avant remise)
 *   lang     : "fr"|"en"|"de"|"it"
 *   compact  : boolean — mode compact (checkout drawer)
 */
export default function VolumeDiscountRow({ subtotal, lang = "fr", compact = false }) {
  const L = LABELS[lang] || LABELS.fr;
  const discount = computeVolumeDiscount(subtotal);
  if (subtotal <= 0) return null;

  return (
    <div data-testid="volume-discount-block" className="space-y-1">
      <div className="flex items-center justify-between py-2 border-b border-white/10">
        <span className="text-zinc-400 font-body text-sm">{L.subtotal}</span>
        <span
          data-testid="cart-subtotal-value"
          className={`font-mono ${compact ? "text-sm" : "text-lg"} tabular-nums ${discount.discountRate > 0 ? "text-zinc-500 line-through" : "text-white font-bold"}`}
        >
          {fmt(discount.subtotal)}
        </span>
      </div>

      {discount.discountRate > 0 && (
        <div
          className="flex items-center justify-between py-2 border-b border-[#39FF14]/20"
          data-testid="cart-volume-discount-row"
        >
          <span className="flex items-center gap-2 text-[#39FF14] font-mono text-xs uppercase tracking-wider">
            <TrendingUp size={14} />
            {L.discount} −{Math.round(discount.discountRate * 100)}%
          </span>
          <span
            data-testid="cart-volume-discount-amount"
            className={`font-mono ${compact ? "text-sm" : "text-lg"} tabular-nums text-[#39FF14] font-bold`}
          >
            −{fmt(discount.discountAmount)}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between py-3">
        <span
          className={`font-mono uppercase tracking-widest ${compact ? "text-[10px]" : "text-[11px]"} text-zinc-400`}
        >
          {L.net}
        </span>
        <span
          data-testid="cart-net-total"
          className={`font-display ${compact ? "text-xl" : "text-3xl"} font-bold text-[#39FF14] tabular-nums neon-text-soft`}
        >
          {fmt(discount.netTotal)}
        </span>
      </div>

      {/* Message incitatif palier suivant */}
      {discount.nextTier && (
        <div
          data-testid="cart-next-tier-hint"
          className="mt-2 p-3 border border-[#39FF14]/25 bg-[#39FF14]/[0.03] rounded"
        >
          <p className="text-xs text-zinc-300 font-body leading-relaxed">
            <TrendingUp size={12} className="inline-block mr-1 text-[#39FF14]" />
            {L.nextTier}{" "}
            <span className="text-[#39FF14] font-mono font-bold">
              {fmt(discount.nextTier.missing)}
            </span>{" "}
            {L.nextTier2}{" "}
            <span className="text-[#39FF14] font-mono font-bold">
              −{Math.round(discount.nextTier.rate * 100)}%
            </span>
          </p>
        </div>
      )}

      {discount.discountRate > 0 && !discount.nextTier && (
        <div className="mt-2 p-3 border border-[#39FF14]/40 bg-[#39FF14]/[0.06] rounded">
          <p className="text-xs text-[#39FF14] font-mono uppercase tracking-wider">
            ✓ {L.tierApplied}
          </p>
        </div>
      )}
    </div>
  );
}
